import React, { useState, useMemo, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, Legend } from 'recharts';
import { ChevronDown, Search, Sliders, Presentation, Wind, LayoutGrid, List, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from "firebase/app";
import {
    Spinner,
    useColorMode,
    Button,
    useToast,
    Center,
    Image,
    Text,
    Progress,
    Box,
    Flex,
    Heading,
    HStack,
    VStack,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    IconButton,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Tooltip,
    Badge,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    SimpleGrid,
    Icon,
    useColorModeValue,
    TableContainer,
    Divider
} from '@chakra-ui/react';
import UwmLoanDetailsDrawer from '../components/Client/UwmLoanDetailsDrawer';

// Main App Component
function OpsCommandCenter() {
    const { currentUser, SuperAdmin } = useAuth();
    const { colorMode } = useColorMode();
    const toast = useToast();
    const [allLoans, setAllLoans] = useState([]);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [filterLoanOfficer, setFilterLoanOfficer] = useState('All Loan Officers');
    const [filterProcessor, setFilterProcessor] = useState('All CSCs');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'default', direction: 'ascending' });
    const [showModal, setShowModal] = useState(false);
    const [modalContent, setModalContent] = useState('');
    const [modalTitle, setModalTitle] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isUwmDrawerOpen, setIsUwmDrawerOpen] = useState(false);
    const [selectedLoanForUwm, setSelectedLoanForUwm] = useState(null);
    const [flowView, setFlowView] = useState('list');
    const [dashboardView, setDashboardView] = useState('list'); // Default to 'list'
    const [timestamp, setTimestamp] = useState(null);
    const mainBg = useColorModeValue('gray.50', 'gray.900');
    const textColor = useColorModeValue('gray.800', 'gray.300');
    const headerColor = useColorModeValue('gray.900', 'white');
    const subtextColor = useColorModeValue('gray.600', 'gray.400');
    const containerBg = useColorModeValue('whiteAlpha.600', 'gray.900/50');
    const containerBorder = useColorModeValue('gray.200/50', 'gray.700/50');
    const inputBg = useColorModeValue('gray.100', 'gray.800/50');
    const inputBorder = useColorModeValue('gray.300', 'gray.700');
    const tableBorder = useColorModeValue('gray.200', 'gray.700');
    const rowHoverBg = useColorModeValue('gray.50', 'gray.800/50');
    const chartGridColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(255, 255, 255, 0.1)');
    const chartTextColor = useColorModeValue('#4b5563', '#9CA3AF');
    const cardBg = useColorModeValue('white', 'gray.800/50');
    const cardBorder = useColorModeValue('gray.200', 'gray.700/50');
    const flowNavBg = useColorModeValue('gray.200/70', 'gray.800/50');

    const handleOpenUwmDrawer = (loan) => {
        setSelectedLoanForUwm(loan);
        setIsUwmDrawerOpen(true);
    };

    const handleCloseUwmDrawer = () => {
        setIsUwmDrawerOpen(false);
        setSelectedLoanForUwm(null);
    };

    // --- Helper function to copy text to clipboard and show a toast ---
    const copyToClipboard = (text, fieldName) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            toast({
                title: `${fieldName} Copied!`,
                description: `"${text}" has been copied to your clipboard.`,
                status: 'success',
                duration: 2000,
                isClosable: true,
                position: 'top',
            });
        } catch (err) {
            console.error('Failed to copy text: ', err);
            toast({
                title: 'Copy Failed',
                description: 'Could not copy text to clipboard.',
                status: 'error',
                duration: 2000,
                isClosable: true,
                position: 'top',
            });
        }
        document.body.removeChild(textArea);
    };

    // This effect runs once when the component mounts. It checks the window width
    // and sets the dashboard view to 'card' if the screen is smaller than 768px.
    useEffect(() => {
        const isMobile = window.innerWidth < 768; // Chakra's 'md' breakpoint
        if (isMobile) {
            setDashboardView('card');
        }
    }, []); // Empty dependency array ensures this runs only on mount

    useEffect(() => {
        if (!currentUser?.uid) {
            console.log("OpsCommandCenter: Waiting for user authentication to be fully established...");
            setIsLoading(false);
            return;
        }

        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const app = getApp();
                const functions = getFunctions(app, 'us-central1');
                const getOpsData = httpsCallable(functions, 'getOpsCommandCenterData');

                const result = await getOpsData({
                    email: currentUser.email,
                    isSuperAdmin: SuperAdmin
                });

                const data = result.data;
                if (data) {
                    if (Array.isArray(data)) {
                        setAllLoans(data);
                    } else if (data.allLoans) {
                        setAllLoans(data.allLoans);
                        setTimestamp(data.timestamp);
                    }
                }
            } catch (err) {
                console.error("Error fetching Ops data:", err.code, err.message);
                setError(err.code === 'unauthenticated' ? "Authentication error. Please try logging out and logging back in." : "Could not load pipeline data. Please try again later.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [currentUser, SuperAdmin]);

    const loanOfficers = useMemo(() => ['All Loan Officers', ...[...new Set(allLoans.map(loan => loan.loanOfficer.name).filter(Boolean))].sort()], [allLoans]);
    const processors = useMemo(() => ['All CSCs', ...[...new Set(allLoans.map(loan => loan.processor).filter(Boolean))].sort()], [allLoans]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const resetSort = () => {
        setSortConfig({ key: 'default', direction: 'ascending' });
    };

    const sortedAndFilteredLoans = useMemo(() => {
        let sortableItems = [...allLoans].filter(loan => {
            const matchesLoanOfficer = filterLoanOfficer === 'All Loan Officers' || loan.loanOfficer.name === filterLoanOfficer;
            const matchesProcessor = filterProcessor === 'All CSCs' || loan.processor === filterProcessor;
            const matchesSearchTerm = searchTerm === '' ||
                (loan.id && loan.id.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (loan.borrower && loan.borrower.toLowerCase().includes(searchTerm.toLowerCase()));
            return matchesLoanOfficer && matchesProcessor && matchesSearchTerm;
        });

        if (sortConfig.key === 'default') {
            const statusOrder = { red: 1, yellow: 2, green: 3, grey: 4 };
            sortableItems.sort((a, b) => {
                const statusA = statusOrder[a.overallStatus] || 99;
                const statusB = statusOrder[b.overallStatus] || 99;
                if (statusA !== statusB) return statusA - statusB;
                const dateA = a.dates.closingDate ? new Date(a.dates.closingDate) : null;
                const dateB = b.dates.closingDate ? new Date(b.dates.closingDate) : null;
                if (dateA && dateB) return dateA - dateB;
                if (dateA) return -1;
                if (dateB) return 1;
                return 0;
            });
        } else if (sortConfig.key) {
            const getNestedValue = (obj, path) => path.split('.').reduce((acc, part) => acc && acc[part], obj);
            sortableItems.sort((a, b) => {
                const aValue = getNestedValue(a, sortConfig.key);
                const bValue = getNestedValue(b, sortConfig.key);
                if (aValue === null || typeof aValue === 'undefined') return 1;
                if (bValue === null || typeof bValue === 'undefined') return -1;
                if (['dates.closingDate', 'dates.financingContingency', 'lastUpdated'].includes(sortConfig.key)) {
                    const dateA = new Date(aValue);
                    const dateB = new Date(bValue);
                    return sortConfig.direction === 'ascending' ? dateA - dateB : dateB - dateA;
                }
                if (String(aValue).toLowerCase() < String(bValue).toLowerCase()) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (String(aValue).toLowerCase() > String(bValue).toLowerCase()) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [allLoans, filterLoanOfficer, filterProcessor, searchTerm, sortConfig]);

    const dayOneLoans = useMemo(() => allLoans
        .filter(loan => {
            if (!loan.disclosedDate) return false;
            const disclosedDate = new Date(loan.disclosedDate);
            const sevenDaysAgo = new Date(new Date().setDate(new Date().getDate() - 7));
            return disclosedDate >= sevenDaysAgo;
        })
        .sort((a, b) => new Date(b.disclosedDate) - new Date(a.disclosedDate)),
        [allLoans]);

    const getStatusPill = (status) => {
        const colorStyles = {
            green: { bg: 'green.400', boxShadow: '0 0 10px var(--chakra-colors-green-400)' },
            yellow: { bg: 'yellow.400', boxShadow: '0 0 10px var(--chakra-colors-yellow-400)' },
            red: { bg: 'red.400', boxShadow: '0 0 10px var(--chakra-colors-red-400)' },
        };
        return <Box w={3} h={3} borderRadius="full" transition="all 0.2s" {...(colorStyles[status] || { bg: 'gray.500' })} />;
    };
    
    const OverallStatusPill = ({ status, children, ...props }) => {
        const styles = useColorModeValue(
            {
                green: { bg: "green.100", color: "green.800", ringColor: "green.600/20" },
                yellow: { bg: "yellow.100", color: "yellow.800", ringColor: "yellow.600/20" },
                red: { bg: "red.100", color: "red.800", ringColor: "red.600/20" },
            },
            {
                green: { bg: "green.500/20", color: "green.300", ringColor: "green.500/30" },
                yellow: { bg: "yellow.500/20", color: "yellow.300", ringColor: "yellow.500/30" },
                red: { bg: "red.500/20", color: "red.300", ringColor: "red.500/30" },
            }
        );
        const defaultStyle = useColorModeValue(
            { bg: "gray.100", color: "gray.800", ringColor: "gray.500/20" },
            { bg: "gray.500/20", color: "gray.300", ringColor: "gray.500/20" }
        );
        const styleProps = styles[status] || defaultStyle;
        return (
            <Badge px={2.5} py={1} borderRadius="full" fontSize="xs" fontWeight="semibold" variant="subtle" bg={styleProps.bg} color={styleProps.color} ring={1} ringInset ringColor={styleProps.ringColor} {...props}>
                {children}
            </Badge>
        );
    };

    const getAppraisalTooltip = (loan) => loan.status.appraisal_colorReason || (loan.status.appraisal === 'green' ? `Completed ${loan.dates.appraisalCompleted}` : (loan.dates.appraisalOrdered ? `Ordered ${loan.dates.appraisalOrdered}` : 'Not Ordered'));
    const getTitleTooltip = (loan) => loan.status.title_colorReason || (loan.status.title === 'green' ? `Completed ${loan.dates.titleCompleted}` : (loan.dates.titleOrdered ? `Ordered ${loan.dates.titleOrdered}` : 'Not Ordered'));
    const getHoiTooltip = (loan) => loan.status.hoi_colorReason || (loan.dates.hoiReceivedDate ? `Completed. Received on ${loan.dates.hoiReceivedDate}` : (loan.dates.hoiOrderedDate ? `Not received. Requested on ${loan.dates.hoiOrderedDate}` : "Not Ordered"));
    const getCdTooltip = (loan) => loan.status.cd_colorReason || (loan.status.cd === 'green' ? 'Signed' : 'Not Sent');
    const getPayoffTooltip = (loan) => loan.status.payoffStatus_colorReason || (loan.status.payoffStatus === 'green' ? `Received ${loan.dates.payoffsReceivedDate}` : (loan.dates.payoffsOrderedDate ? `Ordered ${loan.dates.payoffsOrderedDate}` : 'Not Ordered'));

    const getFlowTitleStatus = (loan) => {
        if (!loan.disclosedDate || !loan.dates.titleOrdered) return 'red';
        const disclosed = new Date(loan.disclosedDate.replace(/-/g, '/'));
        const titleOrdered = new Date(loan.dates.titleOrdered.replace(/-/g, '/'));
        disclosed.setHours(0, 0, 0, 0);
        titleOrdered.setHours(0, 0, 0, 0);
        return titleOrdered <= disclosed ? 'green' : 'red';
    }

    const getRateLockTooltip = (loan) => loan.status.rateLock_colorReason || (
        <VStack spacing={1} align="start">
            {loan.dates.rateLockDate ? (<> <Text>Locked: {loan.dates.rateLockDate}</Text> <Text>Expires: {loan.dates.rateLockExpirationDate}</Text> <Text>Closing: {loan.dates.closingDate}</Text> </>) : (<Text>Not Locked</Text>)}
        </VStack>
    );
    const getCondoApprovalTooltip = (loan) => loan.status.condoApproval_colorReason || `Condo Approval: ${loan.status.condoApproval}`;
    const getTitleContactTooltip = (loan) => `Title Contact Info: ${loan.status.titleContact === 'green' ? 'Provided' : 'Not Provided'}`;
    const getAppraisalContactTooltip = (loan) => `Appraisal Contact Info: ${loan.status.appraisalContact === 'green' ? 'Provided' : 'Not Provided'}`;
    const getCreditCardAuthTooltip = (loan) => `Credit Card Auth: ${loan.status.creditCardCollection === 'green' ? 'Provided' : 'Not Provided'}`;

    const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return new Intl.DateTimeFormat('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', timeZone: 'UTC' }).format(date);
    };

    const getOverallTooltip = (loan) => (
        <VStack spacing={2} align="start">
            <Text><Text as="span" fontWeight="bold">Appraisal:</Text> {loan.status.appraisal === 'green' ? `Completed ${loan.dates.appraisalCompleted}` : (loan.dates.appraisalOrdered ? `Ordered ${loan.dates.appraisalOrdered}` : 'Not Ordered')}</Text>
            <Text><Text as="span" fontWeight="bold">Title:</Text> {loan.status.title === 'green' ? `Completed ${loan.dates.titleCompleted}` : (loan.dates.titleOrdered ? `Ordered ${loan.dates.titleOrdered}` : 'Not Ordered')}</Text>
            <Text><Text as="span" fontWeight="bold">HOI:</Text> {loan.dates.hoiReceivedDate ? `Received ${formatDate(loan.dates.hoiReceivedDate)}` : (loan.dates.hoiOrderedDate ? `Ordered on ${formatDate(loan.dates.hoiOrderedDate)}` : "Not Ordered")}</Text>
            <Text><Text as="span" fontWeight="bold">CD:</Text> {loan.status.cd === 'green' ? 'Signed' : 'Not Sent'}</Text>
            {loan.status.payoffStatus !== 'n/a' && <Text><Text as="span" fontWeight="bold">Payoff:</Text> {loan.status.payoffStatus === 'green' ? `Received ${loan.dates.payoffsReceivedDate}` : (loan.dates.payoffsOrderedDate ? `Ordered ${loan.dates.payoffsOrderedDate}` : 'Not Ordered')}</Text>}
            <Box pt={1} borderTopWidth={1} borderColor="gray.700" w="full">
                <Text><Text as="span" fontWeight="bold">Rate Lock:</Text> {loan.dates.rateLockDate ? `Locked: ${loan.dates.rateLockDate}` : 'Not Locked'}</Text>
                {loan.dates.rateLockExpirationDate && <Text>Expires: {loan.dates.rateLockExpirationDate}</Text>}
            </Box>
        </VStack>
    );

    const leadershipData = useMemo(() => Object.values(
        allLoans.reduce((acc, loan) => {
            if (!acc[loan.processor]) acc[loan.processor] = { name: loan.processor, green: 0, yellow: 0, red: 0 };
            const status = loan.overallStatusProcessors || loan.overallStatus;
            if (acc[loan.processor][status] !== undefined) acc[loan.processor][status]++;
            return acc;
        }, {})
    ).sort((a, b) => (b.red - a.red) || (b.yellow - a.yellow) || (a.green - b.green)), [allLoans]);

    const chartHeight = Math.max(300, leadershipData.length * 60);

    const columns = [
        { label: 'Loan / Borrower', key: 'borrower', sortable: true },
        { label: '', key: 'quickClose', sortable: false },
        { label: 'Loan Officer', key: 'loanOfficer.name', sortable: true },
        { label: 'CSC', key: 'processor', sortable: true },
        { label: 'Loan Status', key: 'stageName', sortable: true },
        { label: 'Status Breakdown', key: 'statusBreakdown', sortable: false },
        { label: 'Closing Date', key: 'dates.closingDate', sortable: true },
        { label: 'Financing Contingency', key: 'dates.financingContingency', sortable: true },
        { label: 'Arive Link', key: 'ariveLink', sortable: false },
        { label: 'Integrations', key: 'integrations', sortable: false },
    ];

    return (
        <Box bg={mainBg} color={textColor} fontFamily="sans-serif" minH="100vh" p={{ base: 4, sm: 6, md: 8 }}>
            <Flex direction={{ base: 'column', lg: 'row' }} justify="space-between" align={{ base: 'start', lg: 'center' }} mb={8} gap={4}>
                <Box>
                    <Heading as="h1" size="xl" fontWeight="bold" letterSpacing="tight" color={headerColor}>Ops 2.0 Command Center</Heading>
                    <Text color={subtextColor} mt={1}>Daily Loan Pipeline Intelligence</Text>
                    {timestamp && <Text fontSize="xs" mt={1.5} color={subtextColor}>Updated: {timestamp}</Text>}
                </Box>
                <HStack p={1.5} borderRadius="full" spacing={2} borderWidth={1} bg={containerBg} borderColor={containerBorder} flexShrink={0}>
                    {[{ id: 'dashboard', label: 'Dashboard', icon: Sliders }, { id: 'dayOneReport', label: 'Flow', icon: Wind }, { id: 'leadershipView', label: 'Intel', icon: Presentation }]
                        .filter(tab => SuperAdmin || tab.id === 'dashboard')
                        .filter(tab => !(tab.id === 'leadershipView' && (!allLoans || allLoans.length === 0)))
                        .map(tab => (
                            <Button key={tab.id} onClick={() => setActiveTab(tab.id)} size="sm" borderRadius="full" leftIcon={<Icon as={tab.icon} boxSize={4} />} colorScheme={activeTab === tab.id ? 'blue' : 'gray'} variant={activeTab === tab.id ? 'solid' : 'ghost'}>
                                {tab.label}
                            </Button>
                        ))}
                </HStack>
            </Flex>

            {isLoading ? (
                <Center flexDir={'column'} mt='250px'>
                    <Image src={require('../assets/img/rocket-load.gif')} />
                    <Text colorScheme={'gray'} fontSize={'sm'}>LOADING</Text>
                    <Progress w='50px' size={'xs'} isIndeterminate />
                </Center>
            ) : error ? (
                <Center><Text color="red.500">{error}</Text></Center>
            ) : (
                <Box borderWidth={1} borderRadius="2xl" p={6} bg={containerBg} borderColor={containerBorder} boxShadow="2xl">
                    {activeTab === 'dashboard' && (
                        <VStack spacing={6} align="stretch">
                            <Flex direction={{ base: 'column', xl: 'row' }} justify="space-between" align="center" gap={4}>
                                <HStack spacing={4}>
                                    <Heading as="h2" size="lg" fontWeight="semibold" color={headerColor} flexShrink={0}>Active Pipeline</Heading>
                                    <HStack p={1} borderRadius="full" bg={flowNavBg}>
                                        <IconButton onClick={() => setDashboardView('card')} size="sm" borderRadius="full" icon={<Icon as={LayoutGrid} boxSize={4} />} colorScheme={dashboardView === 'card' ? 'blue' : 'gray'} variant={dashboardView === 'card' ? 'solid' : 'ghost'} aria-label="Card View" />
                                        <IconButton onClick={() => setDashboardView('list')} size="sm" borderRadius="full" icon={<Icon as={List} boxSize={4} />} colorScheme={dashboardView === 'list' ? 'blue' : 'gray'} variant={dashboardView === 'list' ? 'solid' : 'ghost'} aria-label="List View" />
                                    </HStack>
                                </HStack>
                                <HStack spacing={4} w="100%" justify={{ base: 'center', xl: 'flex-end' }}>
                                    <InputGroup w={{ base: '100%', md: '350px' }}>
                                        <InputLeftElement pointerEvents="none" children={<Icon as={Search} color="gray.500" />} />
                                        <Input type="text" placeholder="Search ID or Borrower..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} borderRadius="full" bg={inputBg} borderColor={inputBorder} />
                                    </InputGroup>
                                    {SuperAdmin && (
                                        <Select value={filterLoanOfficer} onChange={e => setFilterLoanOfficer(e.target.value)} borderRadius="full" bg={inputBg} borderColor={inputBorder} w="max-content">
                                            {loanOfficers.map(p => <option key={p} value={p}>{p}</option>)}
                                        </Select>
                                    )}
                                    <Select value={filterProcessor} onChange={e => setFilterProcessor(e.target.value)} borderRadius="full" bg={inputBg} borderColor={inputBorder} w="max-content">
                                        {processors.map(p => <option key={p} value={p}>{p}</option>)}
                                    </Select>
                                    <IconButton icon={<Icon as={RotateCcw} />} onClick={resetSort} aria-label="Reset Sort" isRound />
                                </HStack>
                            </Flex>

                            {dashboardView === 'list' ? (
                                <TableContainer>
                                    <Table variant="simple" size="sm">
                                        <Thead borderBottomWidth={1} borderColor={tableBorder}>
                                            <Tr>
                                                {columns.map((col) => (
                                                    <Th
                                                        key={col.key || col.label}
                                                        px={2}
                                                        py={3}
                                                        textTransform="uppercase"
                                                        fontSize="xs"
                                                        color={subtextColor}
                                                        whiteSpace={col.key === 'dates.financingContingency' ? 'normal' : 'nowrap'}
                                                    >
                                                        {col.sortable ? (
                                                            <Button
                                                                variant="link"
                                                                colorScheme="gray"
                                                                onClick={() => requestSort(col.key)}
                                                                rightIcon={sortConfig.key === col.key ? (sortConfig.direction === 'ascending' ? <ArrowUp size="12" /> : <ArrowDown size="12" />) : null}
                                                            >
                                                                {col.key === 'dates.financingContingency' ? (
                                                                    <Box lineHeight="short" textAlign="center">
                                                                        <Text m={0}>Financing</Text>
                                                                        <Text m={0}>Contingency</Text>
                                                                    </Box>
                                                                ) : (
                                                                    col.label
                                                                )}
                                                            </Button>
                                                        ) : (
                                                            col.key === 'dates.financingContingency' ? (
                                                                <Box lineHeight="short" textAlign="center">
                                                                    <Text m={0}>Financing</Text>
                                                                    <Text m={0}>Contingency</Text>
                                                                </Box>
                                                            ) : (
                                                                col.label
                                                            )
                                                        )}
                                                    </Th>
                                                ))}
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {sortedAndFilteredLoans.map((loan, index) => (
                                                <Tr key={loan.id || `loan-${index}`} _hover={{ bg: rowHoverBg }} transition="background-color 0.2s">
                                                    <Td px={2} py={4}>
                                                        <Text fontWeight="bold" color={headerColor} cursor="pointer" _hover={{ color: 'blue.500' }} onClick={() => copyToClipboard(loan.borrower, 'Borrower')}>{loan.borrower}</Text>
                                                        <Text fontSize="sm" color={subtextColor} cursor="pointer" _hover={{ color: 'blue.500' }} onClick={() => copyToClipboard(loan.id, 'Loan #')}>{loan.id}</Text>
                                                        <Text fontSize="sm" color={subtextColor} cursor="pointer" _hover={{ color: 'blue.500' }} onClick={() => copyToClipboard(loan.lender, 'Lender')}>{loan.lender}</Text>
                                                    </Td>
                                                    <Td px={2} py={4}>
                                                        {loan.daysFromDisclosedToClosing && loan.daysFromDisclosedToClosing < 20 && (
                                                            <Tooltip label={`QUICK CLOSE: This loan was disclosed with ${loan.daysFromDisclosedToClosing} days to closing.`}>
                                                                <Box>
                                                                    <OverallStatusPill status='red' animation="pulse 1.5s infinite">!!!</OverallStatusPill>
                                                                </Box>
                                                            </Tooltip>
                                                        )}
                                                    </Td>
                                                    <Td px={2} py={4} fontSize="sm">{loan.loanOfficer.name}</Td>
                                                    <Td px={2} py={4} fontSize="sm">{loan.processor}</Td>
                                                    <Td px={2} py={4} fontSize="sm">{loan.stageName}</Td>
                                                    <Td px={2} py={4}>
                                                        <Flex wrap="nowrap" gap={3}>
                                                            <Tooltip label={getAppraisalTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">A</Text>{getStatusPill(loan.status.appraisal)}</VStack></Tooltip>
                                                            <Tooltip label={getTitleTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">T</Text>{getStatusPill(loan.status.title)}</VStack></Tooltip>
                                                            <Tooltip label={getHoiTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">HOI</Text>{getStatusPill(loan.status.hoi)}</VStack></Tooltip>
                                                            {loan.status.payoffStatus !== 'n/a' && <Tooltip label={getPayoffTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">P</Text>{getStatusPill(loan.status.payoffStatus)}</VStack></Tooltip>}
                                                            <Tooltip label={getCdTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">CD</Text>{getStatusPill(loan.status.cd)}</VStack></Tooltip>
                                                            <Tooltip label={getRateLockTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">L</Text>{getStatusPill(loan.status.rateLock)}</VStack></Tooltip>
                                                            {loan.status.condoApproval !== 'n/a' && <Tooltip label={getCondoApprovalTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">C</Text>{getStatusPill(loan.status.condoApproval)}</VStack></Tooltip>}
                                                            {loan.status.fhaCaseNumber !== 'n/a' && <Tooltip label={loan.status.fhaCaseNumber_colorReason || 'FHA Case # Received'}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">F</Text>{getStatusPill(loan.status.fhaCaseNumber)}</VStack></Tooltip>}
                                                            <Tooltip label={getOverallTooltip(loan)}><Box><OverallStatusPill status={loan.overallStatus}>Info</OverallStatusPill></Box></Tooltip>
                                                        </Flex>
                                                    </Td>
                                                    <Td px={2} py={4} fontSize="sm" color={subtextColor}>{formatDate(loan.dates.closingDate)}</Td>
                                                    <Td px={2} py={4} fontSize="sm" color={subtextColor}>{formatDate(loan.dates.financingContingency)}</Td>
                                                    <Td px={2} py={4}>
                                                        <Button size="sm" colorScheme="blue" borderRadius="full" onClick={(e) => { e.stopPropagation(); window.open(`https://future.myarive.com/app/loans/${loan.ariveLoanId}/loan-center`, '_blank'); }}>
                                                            Arive
                                                        </Button>
                                                    </Td>
                                                    <Td px={2} py={4}>
                                                        {(loan.lender === 'United Wholesale Mortgage' || loan.lender === 'UWM' || (loan.id && loan.id.startsWith('152'))) && (
                                                            <Button size="sm" colorScheme="purple" borderRadius="full" onClick={() => handleOpenUwmDrawer(loan)}>
                                                                UWM
                                                            </Button>
                                                        )}
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
                                    {sortedAndFilteredLoans.map((loan) => (
                                        <Box key={loan.id} p={5} borderRadius="xl" borderWidth="1px" bg={cardBg} borderColor={cardBorder} _hover={{ borderColor: 'blue.500' }} transition="all 0.2s">
                                            <VStack spacing={4} align="stretch">
                                                <Flex justify="space-between" align="start">
                                                    <Box>
                                                        <Heading size="md" color={headerColor} cursor="pointer" _hover={{ color: 'blue.500' }} onClick={() => copyToClipboard(loan.borrower, 'Borrower')}>{loan.borrower}</Heading>
                                                        <Text fontSize="sm" color={subtextColor} cursor="pointer" _hover={{ color: 'blue.500' }} onClick={() => copyToClipboard(loan.id, 'Loan #')}>{loan.id}</Text>
                                                        <Text fontSize="xs" color={subtextColor}>{loan.lender}</Text>
                                                    </Box>
                                                    {loan.daysFromDisclosedToClosing && loan.daysFromDisclosedToClosing < 20 && (
                                                        <Tooltip label={`QUICK CLOSE: Disclosed with ${loan.daysFromDisclosedToClosing} days to closing.`}>
                                                            <Box>
                                                                <OverallStatusPill status='red' animation="pulse 1.5s infinite">!!!</OverallStatusPill>
                                                            </Box>
                                                        </Tooltip>
                                                    )}
                                                </Flex>
                                                <Divider />
                                                <VStack spacing={1} align="stretch" fontSize="sm">
                                                    <HStack justify="space-between"><Text fontWeight="bold">Closing:</Text><Text>{formatDate(loan.dates.closingDate)}</Text></HStack>
                                                    <HStack justify="space-between"><Text fontWeight="bold">LO:</Text><Text>{loan.loanOfficer.name}</Text></HStack>
                                                    <HStack justify="space-between"><Text fontWeight="bold">CSC:</Text><Text>{loan.processor}</Text></HStack>
                                                    <HStack justify="space-between"><Text fontWeight="bold">Stage:</Text><Text>{loan.stageName}</Text></HStack>
                                                    <HStack justify="space-between"><Text fontWeight="bold">Financing Contingency:</Text><Text>{formatDate(loan.dates.financingContingency)}</Text></HStack>
                                                </VStack>
                                                <Divider />
                                                <Flex wrap="wrap" gap={3} justify="center">
                                                    <Tooltip label={getAppraisalTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">A</Text>{getStatusPill(loan.status.appraisal)}</VStack></Tooltip>
                                                    <Tooltip label={getTitleTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">T</Text>{getStatusPill(loan.status.title)}</VStack></Tooltip>
                                                    <Tooltip label={getHoiTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">HOI</Text>{getStatusPill(loan.status.hoi)}</VStack></Tooltip>
                                                    {loan.status.payoffStatus !== 'n/a' && <Tooltip label={getPayoffTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">P</Text>{getStatusPill(loan.status.payoffStatus)}</VStack></Tooltip>}
                                                    <Tooltip label={getCdTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">CD</Text>{getStatusPill(loan.status.cd)}</VStack></Tooltip>
                                                    <Tooltip label={getRateLockTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">L</Text>{getStatusPill(loan.status.rateLock)}</VStack></Tooltip>
                                                    {loan.status.condoApproval !== 'n/a' && <Tooltip label={getCondoApprovalTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">C</Text>{getStatusPill(loan.status.condoApproval)}</VStack></Tooltip>}
                                                    {loan.status.fhaCaseNumber !== 'n/a' && <Tooltip label={loan.status.fhaCaseNumber_colorReason || 'FHA Case # Received'}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">F</Text>{getStatusPill(loan.status.fhaCaseNumber)}</VStack></Tooltip>}
                                                    <Tooltip label={getOverallTooltip(loan)}><Box><OverallStatusPill status={loan.overallStatus}>Info</OverallStatusPill></Box></Tooltip>
                                                </Flex>
                                                <HStack mt={2} spacing={2}>
                                                    <Button flex={1} size="sm" colorScheme="blue" borderRadius="full" onClick={() => window.open(`https://future.myarive.com/app/loans/${loan.ariveLoanId}/loan-center`, '_blank')}>
                                                        Arive
                                                    </Button>
                                                    {(loan.lender === 'United Wholesale Mortgage' || loan.lender === 'UWM' || (loan.id && loan.id.startsWith('152'))) && (
                                                        <Button flex={1} size="sm" colorScheme="purple" borderRadius="full" onClick={() => handleOpenUwmDrawer(loan)}>
                                                            UWM
                                                        </Button>
                                                    )}
                                                </HStack>
                                            </VStack>
                                        </Box>
                                    ))}
                                </SimpleGrid>
                            )}
                        </VStack>
                    )}
                    {activeTab === 'leadershipView' && (
                        <VStack spacing={6} align="stretch">
                            <Box>
                                <Heading as="h2" size="lg" fontWeight="semibold" color={headerColor}>Pipeline Intelligence</Heading>
                                <Text color={subtextColor}>High-level overview of CSC performance and pipeline health.</Text>
                            </Box>
                            <Box w="100%" h={chartHeight}>
                                <ResponsiveContainer>
                                    <BarChart layout="vertical" data={leadershipData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                        <XAxis type="number" stroke={chartTextColor} fontSize={12} />
                                        <YAxis type="category" dataKey="name" stroke={chartTextColor} fontSize={12} width={80} />
                                        <ChartTooltip cursor={{ fill: 'rgba(107, 114, 128, 0.1)' }} contentStyle={{ backgroundColor: colorMode === 'light' ? '#F9FAFB' : '#1F2937', border: '1px solid #374151', borderRadius: '0.5rem' }} />
                                        <Legend wrapperStyle={{ fontSize: "14px", paddingTop: "20px" }} />
                                        <Bar dataKey="green" stackId="a" fill="#22C55E" name="Green" />
                                        <Bar dataKey="yellow" stackId="a" fill="#FBBF24" name="Yellow" />
                                        <Bar dataKey="red" stackId="a" fill="#EF4444" name="Red" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </VStack>
                    )}
                    {activeTab === 'dayOneReport' && (
                        <Box as="section">
                            <Flex justify="space-between" align="center" mb={6}>
                                <Heading as="h2" size="lg" fontWeight="semibold" color={headerColor}>New Loan Flow (Last 7 Days)</Heading>
                                <HStack p={1} borderRadius="full" bg={flowNavBg}>
                                    <Button onClick={() => setFlowView('card')} size="sm" borderRadius="full" leftIcon={<Icon as={LayoutGrid} boxSize={4} />} colorScheme={flowView === 'card' ? 'blue' : 'gray'} variant={flowView === 'card' ? 'solid' : 'ghost'}>Card</Button>
                                    <Button onClick={() => setFlowView('list')} size="sm" borderRadius="full" leftIcon={<Icon as={List} boxSize={4} />} colorScheme={flowView === 'list' ? 'blue' : 'gray'} variant={flowView === 'list' ? 'solid' : 'ghost'}>List</Button>
                                </HStack>
                            </Flex>

                            {flowView === 'card' ? (
                                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
                                    {dayOneLoans.map((loan, index) => (
                                        <Box key={loan.id || `day-one-${index}`} p={5} borderRadius="xl" borderWidth="1px" bg={cardBg} borderColor={cardBorder} _hover={{ borderColor: 'blue.500' }} transition="all 0.2s" cursor="pointer" onClick={() => window.open(`https://future.myarive.com/app/loans/${loan.ariveLoanId}/loan-center`, '_blank')}>
                                            <VStack spacing={4} align="stretch">
                                                <Box>
                                                    <Heading size="md" color={headerColor}>{loan.id}</Heading>
                                                    <Text>{loan.borrower}</Text>
                                                    <Text fontSize="xs" color="gray.500">{loan.loanOfficer.name}</Text>
                                                    <Text fontSize="sm" color="gray.500" mt={1}>Disclosed: {formatDate(loan.disclosedDate)}</Text>
                                                </Box>
                                                <VStack pt={4} borderTopWidth="1px" borderColor={tableBorder} align="stretch">
                                                    <Text fontSize="sm" fontWeight="semibold">Initial Tasks:</Text>
                                                    <HStack spacing={5}>
                                                        <Tooltip label={getTitleContactTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">TC</Text>{getStatusPill(loan.status.titleContact)}</VStack></Tooltip>
                                                        <Tooltip label={getAppraisalContactTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">AC</Text>{getStatusPill(loan.status.appraisalContact)}</VStack></Tooltip>
                                                        <Tooltip label={getCreditCardAuthTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">CC</Text>{getStatusPill(loan.status.creditCardCollection)}</VStack></Tooltip>
                                                        {loan.status.fhaCaseNumber !== 'n/a' && <Tooltip label={loan.status.fhaCaseNumber === 'green' ? 'FHA Case # requested' : 'FHA Case # not requested'}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">F</Text>{getStatusPill(loan.status.fhaCaseNumber)}</VStack></Tooltip>}
                                                        <Tooltip label={getFlowTitleStatus(loan) === 'green' ? 'Title ordered' : 'Title not ordered'}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">T</Text>{getStatusPill(getFlowTitleStatus(loan))}</VStack></Tooltip>
                                                        {loan.daysFromDisclosedToClosing && loan.daysFromDisclosedToClosing < 20 && (
                                                            <Tooltip label={`QUICK CLOSE: Disclosed with ${loan.daysFromDisclosedToClosing} days to closing.`}><VStack spacing={0.5}><Text fontSize="xs" fontWeight="bold" color="red.500">!!</Text>{getStatusPill('red')}</VStack></Tooltip>
                                                        )}
                                                    </HStack>
                                                </VStack>
                                            </VStack>
                                        </Box>
                                    ))}
                                </SimpleGrid>
                            ) : (
                                <TableContainer>
                                    <Table variant="simple">
                                        <Thead>
                                            <Tr>
                                                <Th>Loan / Borrower</Th>
                                                <Th>Loan Officer</Th>
                                                <Th>Disclosed</Th>
                                                <Th>Initial Tasks</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {dayOneLoans.map((loan, index) => (
                                                <Tr key={loan.id || `day-one-list-${index}`} _hover={{ bg: rowHoverBg }} cursor="pointer" onClick={() => window.open(`https://future.myarive.com/app/loans/${loan.ariveLoanId}/loan-center`, '_blank')}>
                                                    <Td>
                                                        <Text fontWeight="bold">{loan.id}</Text>
                                                        <Text fontSize="sm" color={subtextColor}>{loan.borrower}</Text>
                                                    </Td>
                                                    <Td>{loan.loanOfficer.name}</Td>
                                                    <Td>{formatDate(loan.disclosedDate)}</Td>
                                                    <Td>
                                                        <HStack spacing={5}>
                                                            <Tooltip label={getTitleContactTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">TC</Text>{getStatusPill(loan.status.titleContact)}</VStack></Tooltip>
                                                            <Tooltip label={getAppraisalContactTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">AC</Text>{getStatusPill(loan.status.appraisalContact)}</VStack></Tooltip>
                                                            <Tooltip label={getCreditCardAuthTooltip(loan)}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">CC</Text>{getStatusPill(loan.status.creditCardCollection)}</VStack></Tooltip>
                                                            {loan.status.fhaCaseNumber !== 'n/a' && <Tooltip label={loan.status.fhaCaseNumber === 'green' ? 'FHA Case # requested' : 'FHA Case # not requested'}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">F</Text>{getStatusPill(loan.status.fhaCaseNumber)}</VStack></Tooltip>}
                                                            <Tooltip label={getFlowTitleStatus(loan) === 'green' ? 'Title ordered' : 'Title not ordered'}><VStack spacing={1}><Text fontSize="10px" fontFamily="mono" color="gray.500">T</Text>{getStatusPill(getFlowTitleStatus(loan))}</VStack></Tooltip>
                                                            {loan.daysFromDisclosedToClosing && loan.daysFromDisclosedToClosing < 20 && (
                                                                <Tooltip label={`QUICK CLOSE: Disclosed with ${loan.daysFromDisclosedToClosing} days to closing.`}><VStack spacing={0.5}><Text fontSize="xs" fontWeight="bold" color="red.500">!!</Text>{getStatusPill('red')}</VStack></Tooltip>
                                                            )}
                                                        </HStack>
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    )}
                </Box>
            )}
            <UwmLoanDetailsDrawer isOpen={isUwmDrawerOpen} onClose={handleCloseUwmDrawer} loan={selectedLoanForUwm} />
        </Box>
    );
}

export default OpsCommandCenter;
